/**
 * TEMP-H-005: Validated API Client
 * 
 * API client wrapper with runtime validation using shared schemas.
 * Provides type-safe API calls with proper error handling and user messaging.
 */

import { 
  ExecutionSettingsSchema,
  StageThresholdsSchema,
  UpdateResponseSchema,
  QCSummarySchema,
  PipelineEventSchema,
  validateApiResponse,
  safeValidateApiResponse,
  type ExecutionSettings,
  type StageThresholds,
  type UpdateResponse,
  type QCSummary,
  type PipelineEvent,
  type StageName,
  type PatchStageSettingsInput
} from './shared-types';
import { joinApiPath } from './api-config';

// =============================================================================
// VALIDATED API CLIENT
// =============================================================================

/**
 * API client error with user-friendly messaging
 */
export class ApiValidationError extends Error {
  constructor(
    public readonly context: string,
    public readonly originalError?: unknown,
    public readonly userMessage?: string
  ) {
    super(`API validation failed for ${context}`);
    this.name = 'ApiValidationError';
  }
  
  /**
   * Get user-friendly error message
   */
  getUserMessage(): string {
    return this.userMessage || `Failed to load ${this.context}. Please try again.`;
  }
}

/**
 * Network error with retry suggestions
 */
export class ApiNetworkError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly context: string,
    public readonly response?: Response
  ) {
    super(`Network error ${status} for ${context}: ${statusText}`);
    this.name = 'ApiNetworkError';
  }
  
  /**
   * Get user-friendly error message with retry suggestions
   */
  getUserMessage(): string {
    if (this.status >= 500) {
      return `Server error occurred while loading ${this.context}. Please try again in a few moments.`;
    } else if (this.status === 404) {
      return `${this.context} not found. It may have been deleted or moved.`;
    } else if (this.status === 403) {
      return `You don't have permission to access ${this.context}.`;
    } else if (this.status === 401) {
      return `Please log in again to access ${this.context}.`;
    } else {
      return `Failed to load ${this.context}. Please check your connection and try again.`;
    }
  }
  
  /**
   * Check if error suggests retry
   */
  shouldRetry(): boolean {
    return this.status >= 500 || this.status === 408 || this.status === 429;
  }
}

/**
 * Base validated API client
 */
export class ValidatedApiClient {
  private readonly baseUrl: string;
  
  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || '/api';
  }
  
  /**
   * Make validated API request
   */
  private async makeRequest<T>(
    schema: any,
    url: string,
    options: RequestInit = {},
    context: string
  ): Promise<T> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    
    try {
      const response = await fetch(fullUrl, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      });
      
      if (!response.ok) {
        throw new ApiNetworkError(
          response.status,
          response.statusText,
          context,
          response
        );
      }
      
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        await response.text();
        throw new ApiValidationError(context, new Error('Non-JSON response'), 
          `Server returned ${contentType || 'unknown'} content instead of JSON for ${context}.`
        );
      }
      
      return validateApiResponse(schema, data, context);
      
    } catch (error) {
      if (error instanceof ApiNetworkError) {
        throw error;
      }
      
      if (error instanceof Error && error.message.includes('validation failed')) {
        throw new ApiValidationError(context, error, 
          `Received invalid data format for ${context}. Please try refreshing the page.`
        );
      }
      
      // Network/JSON parsing errors
      throw new ApiNetworkError(
        0, 
        error instanceof Error ? error.message : 'Unknown error',
        context
      );
    }
  }
  
  /**
   * Safe API request that returns result object
   */
  private async makeSafeRequest<T>(
    schema: any,
    url: string,
    options: RequestInit = {},
    context: string
  ): Promise<{ success: true; data: T } | { success: false; error: ApiValidationError | ApiNetworkError }> {
    try {
      const data = await this.makeRequest<T>(schema, url, options, context);
      return { success: true, data };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof ApiValidationError || error instanceof ApiNetworkError 
          ? error 
          : new ApiValidationError(context, error)
      };
    }
  }
  
  // =========================================================================
  // EXECUTION SETTINGS API
  // =========================================================================
  
  /**
   * Get all execution settings with validation
   */
  async getExecutionSettings(executionId: string): Promise<ExecutionSettings> {
    return this.makeRequest(
      ExecutionSettingsSchema,
      joinApiPath('executions', executionId, 'settings'),
      {},
      'execution settings'
    );
  }
  
  /**
   * Safe execution settings request
   */
  async getExecutionSettingsSafe(executionId: string) {
    return this.makeSafeRequest(
      ExecutionSettingsSchema,
      joinApiPath('executions', executionId, 'settings'),
      {},
      'execution settings'
    );
  }
  
  /**
   * Get stage-specific settings with validation
   */
  async getStageSettings(executionId: string, stage: StageName): Promise<StageThresholds> {
    return this.makeRequest(
      StageThresholdsSchema,
      joinApiPath('executions', executionId, 'stages', stage, 'settings'),
      {},
      `${stage} stage settings`
    );
  }
  
  /**
   * Update stage settings with validation
   */
  async updateStageSettings(
    executionId: string, 
    stage: StageName, 
    updates: PatchStageSettingsInput
  ): Promise<UpdateResponse> {
    return this.makeRequest(
      UpdateResponseSchema,
      joinApiPath('executions', executionId, 'stages', stage, 'settings'),
      {
        method: 'PATCH',
        body: JSON.stringify(updates)
      },
      `${stage} stage settings update`
    );
  }
  
  // =========================================================================
  // QC SUMMARY API  
  // =========================================================================
  
  /**
   * Get QC summary with validation
   */
  async getQCSummary(executionId: string, stage: StageName, iteration?: number): Promise<QCSummary> {
    const url = iteration 
      ? joinApiPath('executions', executionId, 'stages', stage, 'qc', iteration.toString())
      : joinApiPath('executions', executionId, 'stages', stage, 'qc');
      
    return this.makeRequest(
      QCSummarySchema,
      url,
      {},
      `${stage} QC summary`
    );
  }
  
  // =========================================================================
  // EVENTS API
  // =========================================================================
  
  /**
   * Get timeline events with validation (partial validation since we expect array)
   */
  async getTimelineEvents(
    executionId: string,
    filters?: {
      stage?: StageName[];
      eventType?: string[];
      limit?: number;
      cursor?: string;
    }
  ): Promise<{ events: PipelineEvent[]; hasMore: boolean; nextCursor?: string }> {
    const searchParams = new URLSearchParams();
    
    if (filters?.stage) {
      filters.stage.forEach(s => searchParams.append('stage', s));
    }
    if (filters?.eventType) {
      filters.eventType.forEach(et => searchParams.append('eventType', et));
    }
    if (filters?.limit) {
      searchParams.append('limit', filters.limit.toString());
    }
    if (filters?.cursor) {
      searchParams.append('cursor', filters.cursor);
    }
    
    const url = joinApiPath('executions', executionId, 'events') + 
      (searchParams.toString() ? `?${searchParams.toString()}` : '');
    
    const response = await this.makeRequest(
      // Use loose validation for timeline response structure
      // Individual events will be validated below
      {
        parse: (data: any) => data,
        safeParse: (data: any) => ({ success: true, data })
      },
      url,
      {},
      'timeline events'
    );
    
    // Validate individual events
    const validatedEvents: PipelineEvent[] = [];
    const events = (response as any)?.events || [];
    
    for (const event of events) {
      const validation = safeValidateApiResponse(PipelineEventSchema, event, 'timeline event');
      if (validation.success) {
        validatedEvents.push(validation.data);
      } else {
        console.warn('Skipping invalid timeline event:', validation.error);
      }
    }
    
    return {
      events: validatedEvents,
      hasMore: (response as any)?.hasMore || false,
      nextCursor: (response as any)?.nextCursor
    };
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

/**
 * Default validated API client instance
 */
export const apiClient = new ValidatedApiClient();

// =============================================================================
// ERROR HELPERS
// =============================================================================

/**
 * Extract user-friendly error message from API error
 */
export function getApiErrorMessage(error: unknown): string {
  if (error instanceof ApiValidationError || error instanceof ApiNetworkError) {
    return error.getUserMessage();
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Check if API error suggests retry
 */
export function shouldRetryApiError(error: unknown): boolean {
  return error instanceof ApiNetworkError && error.shouldRetry();
}